import type { PatientOwnerChoice } from '@/api/patients/patientsApi.types';
import { isBlank, isValidEmail } from '@/utils/formValidation';
import { isBlankOrValidPhoneNumber } from '@/utils/phoneNumbers';

export type OwnerMode = 'later' | 'existing' | 'new';

export interface OwnerDraft {
  mode: OwnerMode;
  existingClientId: string;
  existingClientLabel: string;
  fullName: string;
  email: string;
  phone: string;
}

export function createOwnerDraft(mode: OwnerMode): OwnerDraft {
  return {
    mode,
    existingClientId: '',
    existingClientLabel: '',
    fullName: '',
    email: '',
    phone: '',
  };
}

export interface OwnerDraftErrors {
  existingClient: string | null;
  fullName: string | null;
  email: string | null;
}

export function validateOwnerDraft(draft: OwnerDraft): OwnerDraftErrors {
  return {
    existingClient:
      draft.mode === 'existing' && isBlank(draft.existingClientId)
        ? 'validation:requiredField'
        : null,
    fullName: draft.mode === 'new' && isBlank(draft.fullName) ? 'validation:requiredField' : null,
    email:
      draft.mode === 'new' && isBlank(draft.email) === false && isValidEmail(draft.email) === false
        ? 'validation:email'
        : null,
  };
}

export function isOwnerDraftValid(draft: OwnerDraft): boolean {
  const errors = validateOwnerDraft(draft);
  return (
    errors.existingClient === null &&
    errors.fullName === null &&
    errors.email === null &&
    (draft.mode !== 'new' || isBlankOrValidPhoneNumber(draft.phone))
  );
}

function toNullable(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

export function toOwnerChoice(draft: OwnerDraft): PatientOwnerChoice {
  if (draft.mode === 'existing') {
    return { kind: 'existing', clientId: draft.existingClientId };
  }
  if (draft.mode === 'new') {
    return {
      kind: 'new',
      client: {
        fullName: draft.fullName.trim(),
        email: toNullable(draft.email),
        phone: toNullable(draft.phone),
        addressLine: null,
        city: null,
        postalCode: null,
        country: null,
        preferredLanguage: null,
      },
    };
  }
  return { kind: 'later' };
}
